/**
 * Nawigator dwóch ujęć (navigator_config_v2.is180 === true).
 *
 * Osobna ścieżka renderowania obok trybu 360° — kod V3 (InteractivePropertyMapV3,
 * drag-to-rotate, krok obrotu 6 klatek, klucz sessionStorage 360°) pozostaje
 * nietknięty. Współdzielimy z V3 tylko to, co bezstanowe:
 *   - PropertySVGOverlayV3 — rysowanie obrysów, kolory statusów, tooltipy.
 *     Dostaje config z JEDNYM artboardem (bieżące ujęcie), więc pracuje w swojej
 *     standardowej ścieżce „po ID": bez drag, bez dropdownu, bez rotacji.
 *   - InteractivePropertyMapV3.css — style paska narzędzi i kontenera mapy.
 *
 * Ten komponent trzyma u siebie: wybór ujęcia, jego zapamiętanie, obraz i toolbar.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropertySVGOverlayV3 from '../v3/PropertySVGOverlayV3';
import { getResponsiveViewBox } from '../../../config/mapConfig';
import '../v3/InteractivePropertyMapV3.css';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.5;

const frameStorageKey = (navigatorConfig) =>
  navigatorConfig?.storageKey ? `nav-180-frame:${navigatorConfig.storageKey}` : null;

function InteractivePropertyMapV180({
  properties = [],
  investmentData,
  selectedPropertyId,
  onPropertySelect,
  hideToolbar = false
}) {
  const navigatorConfig = investmentData?.navigatorConfigV2;
  const artboards = useMemo(() => navigatorConfig?.artboards || [], [navigatorConfig]);

  const [hoveredProperty, setHoveredProperty] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [scale, setScale] = useState(1);
  const [overlayEnabled, setOverlayEnabled] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    window.matchMedia('(max-width: 768px)').matches
  );
  const [frameIdx, setFrameIdx] = useState(() => {
    const key = frameStorageKey(navigatorConfig);
    if (!key) return 0;
    const saved = window.sessionStorage.getItem(key);
    const parsed = saved !== null ? parseInt(saved, 10) : 0;
    const total = navigatorConfig?.artboards?.length || 1;
    if (!Number.isFinite(parsed) || parsed < 0 || parsed >= total) return 0;
    return parsed;
  });
  const containerRef = useRef(null);

  useEffect(() => {
    const key = frameStorageKey(navigatorConfig);
    if (key) {
      window.sessionStorage.setItem(key, String(frameIdx));
    }
  }, [frameIdx, navigatorConfig]);

  // oba ujęcia w cache przeglądarki — przełączenie nie czeka na sieć
  useEffect(() => {
    artboards.forEach((ab) => {
      if (ab.image?.url) {
        const img = new Image();
        img.src = ab.image.url;
      }
    });
  }, [artboards]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobileViewport(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePropertyClick = useCallback((propertyId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (onPropertySelect) {
      onPropertySelect(propertyId);
    }
  }, [onPropertySelect]);

  const handlePropertyHover = useCallback((propertyId) => {
    setHoveredProperty(propertyId);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
    console.error('Investment map image failed to load');
  }, []);

  const handleImageRetry = useCallback(() => {
    setImageError(false);
  }, []);

  const switchView = useCallback(() => {
    const total = artboards.length;
    if (total < 2) return;
    setImageError(false);
    setFrameIdx((prev) => (prev + 1) % total);
  }, [artboards.length]);

  const currentArtboard = artboards[frameIdx] || null;

  // overlay dostaje config z jednym artboardem → jego standardowa ścieżka „po ID"
  const overlayConfig = useMemo(() => {
    if (!navigatorConfig || !currentArtboard) return null;
    return { ...navigatorConfig, is360: false, artboards: [currentArtboard] };
  }, [navigatorConfig, currentArtboard]);

  const zoomIn = () => setScale(prev => Math.min(MAX_SCALE, Math.round((prev + ZOOM_STEP) * 10) / 10));
  const zoomOut = () => setScale(prev => Math.max(MIN_SCALE, Math.round((prev - ZOOM_STEP) * 10) / 10));

  if (imageError) {
    return (
      <div className="interactive-property-map nav-v3">
        <div className="map-error">
          <div className="map-error-content">
            <p>Mapa inwestycji jest tymczasowo niedostępna</p>
            <button className="retry-button" onClick={handleImageRetry} type="button">
              Spróbuj ponownie
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!investmentData || !currentArtboard) {
    return null;
  }

  const mapImageUrl = (isMobileViewport && currentArtboard.image?.urlMobile) ||
                      currentArtboard.image?.url;

  const mapImageAlt = currentArtboard.name || 'Plan inwestycji';

  const viewBox = currentArtboard.viewBox || getResponsiveViewBox(containerWidth);

  const aspectRatio = currentArtboard.image?.width && currentArtboard.image?.height
    ? currentArtboard.image.width / currentArtboard.image.height
    : 1.77;

  return (
    <>
      <div className="interactive-property-map nav-v3" ref={containerRef}>
        <div className="map-container">
          <div
            className="map-zoom-wrapper"
            style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}
          >
            <img
              src={mapImageUrl}
              alt={mapImageAlt}
              className="investment-image"
              style={{ aspectRatio: aspectRatio }}
              onError={handleImageError}
              loading="lazy"
            />

            <PropertySVGOverlayV3
              navigatorConfig={overlayConfig}
              properties={properties}
              selectedProperty={selectedPropertyId}
              hoveredProperty={hoveredProperty}
              onPropertyClick={handlePropertyClick}
              onPropertyHover={handlePropertyHover}
              viewBox={viewBox}
              enableNavigatorTooltips={investmentData.enableNavigatorTooltips}
              overlayEnabled={overlayEnabled}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              canZoomIn={scale < MAX_SCALE}
              canZoomOut={scale > MIN_SCALE}
            />
          </div>
        </div>
      </div>

      {!hideToolbar && (
        <div className="nav-v3-toolbar">
          <div className="nav-v3-group">
            <button
              className="nav-v3-toolbar-btn"
              onClick={switchView}
              aria-label="Obróć o 180°"
            >
              <span className="nav-v3-toolbar-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="nav-v3-toolbar-label">Obróć o 180°</span>
            </button>
            <span className="nav-v3-group-label">{currentArtboard.name}</span>
          </div>
          <div className="nav-v3-group">
            <button
              className="nav-v3-toolbar-btn"
              onClick={zoomIn}
              disabled={scale >= MAX_SCALE}
              aria-label="Przybliż"
            >
              <span className="nav-v3-toolbar-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </span>
              <span className="nav-v3-toolbar-label">Przybliż</span>
            </button>
            <span className="nav-v3-group-label">Zoom</span>
            <button
              className="nav-v3-toolbar-btn"
              onClick={zoomOut}
              disabled={scale <= MIN_SCALE}
              aria-label="Oddal"
            >
              <span className="nav-v3-toolbar-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </span>
              <span className="nav-v3-toolbar-label">Oddal</span>
            </button>
          </div>
          <button
            className={`nav-v3-toolbar-btn nav-v3-toolbar-btn--3d ${overlayEnabled ? '' : 'nav-v3-toolbar-btn--off'}`}
            onClick={() => setOverlayEnabled(v => !v)}
            aria-label={overlayEnabled ? 'Ukryj obrysy 3D' : 'Pokaż obrysy 3D'}
          >
            <span className="nav-v3-mobile-label">Obrysy</span>
            <span className="nav-v3-toolbar-icon nav-v3-3d-icon">
              <span className="nav-v3-3d-text">3D</span>
              {!overlayEnabled && <span className="nav-v3-3d-strike" aria-hidden="true" />}
            </span>
            <span className="nav-v3-toolbar-label">{overlayEnabled ? 'Ukryj obrysy 3D' : 'Pokaż obrysy 3D'}</span>
          </button>
        </div>
      )}
    </>
  );
}

export default InteractivePropertyMapV180;
